// WPS-006/010 · DesignCatalog · client picks template + font for their card.
// Premium atelier UI · CardCanvas real previews · filter by customer category.
import React, { useEffect, useState } from "react";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Palette, Type, Check, Sparkles } from "lucide-react";
import CardCanvas from "./CardCanvas";

const fetchCategories = async () => {
  const base = (process.env.REACT_APP_BACKEND_URL || 'https://print.wunli.com').replace(/\/$/, '') + '/api';
  return fetch(`${base}/categories`).then(r => r.json()).catch(() => []);
};

const DesignCatalog = ({
  templates,
  fonts,
  selectedTemplateId,
  selectedFontId,
  onTemplateSelect,
  onFontSelect,
  onContinue,
  customerCategory = null,        // slug to auto-filter
  customerData = {},              // current card data for preview personalization
}) => {
  const [categories, setCategories] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState(customerCategory || 'all');
  const [styleFilter, setStyleFilter] = useState('all');     // all | bold | minimal

  useEffect(() => {
    fetchCategories().then(setCategories);
  }, []);

  useEffect(() => {
    if (customerCategory && categoryFilter === 'all') setCategoryFilter(customerCategory);
  }, [customerCategory, categoryFilter]);

  const filteredTemplates = (templates || []).filter(t => {
    if (categoryFilter !== 'all' && t.categorySlug !== categoryFilter) return false;
    if (styleFilter !== 'all' && t.style !== styleFilter) return false;
    return true;
  });

  // Sample data for live preview rendering
  const sample = (t) => ({
    companyName: customerData.companyName || 'Your Company',
    personalName: customerData.personalName || customerData.customerName || 'Your Name',
    jobTitle: customerData.jobTitle || 'Owner',
    email: customerData.email || 'you@biz.com',
    phone: customerData.phone || '+1 214 949 7999',
    website: customerData.website || 'yourbiz.com',
    address: customerData.address || '',
    slogan: customerData.slogan || t.defaultSlogan || '',
    logoUrl: customerData.logoUrl || '',
    colors: t.colors || ['#0a0a0a', '#c9a961', '#ffffff'],
    orientation: customerData.orientation || 'horizontal',
    qrPosition: 'back',
    backDesign: { enabled: true, layout: 'minimal-text+qr', showQR: true },
  });

  // Editorial typography classes
  const editorialTitle = {
    fontFamily: 'Inter, system-ui, sans-serif',
    fontSize: '14px',
    fontWeight: 700,
    letterSpacing: '-0.01em',
    color: '#0f172a',
    lineHeight: 1.2,
  };

  const monoLabel = {
    fontFamily: 'JetBrains Mono, monospace',
    fontSize: '9px',
    letterSpacing: '0.15em',
    textTransform: 'uppercase',
    color: '#64748b',
  };

  const activeCat = categories.find(c => c.slug === categoryFilter);

  return (
    <div className="space-y-3">
      <Card>
        <CardContent className="p-4 space-y-4">

          {/* Heading · editorial atelier style */}
          <div>
            <div style={monoLabel}>Step 2 · Look & Feel</div>
            <div style={{
              fontFamily: 'Inter, system-ui, sans-serif',
              fontSize: '24px',
              fontWeight: 800,
              letterSpacing: '-0.02em',
              color: '#0f172a',
              marginTop: '4px',
            }}>
              Choose your design
              {activeCat && (
                <span style={{ color: '#c9a961', marginLeft: '8px', fontWeight: 600 }}>
                  · {activeCat.icon} {activeCat.name}
                </span>
              )}
            </div>
          </div>

          <Tabs defaultValue="templates" className="space-y-3">
            <TabsList className="grid w-full grid-cols-2 h-9">
              <TabsTrigger value="templates" className="text-xs">
                <Palette className="h-3 w-3 mr-1" />
                Templates · {filteredTemplates.length}
              </TabsTrigger>
              <TabsTrigger value="fonts" className="text-xs">
                <Type className="h-3 w-3 mr-1" />
                Typography · {(fonts || []).length}
              </TabsTrigger>
            </TabsList>

            {/* TEMPLATES TAB */}
            <TabsContent value="templates" className="space-y-3">

              {/* Filters · compact */}
              <div className="flex flex-wrap items-center gap-2 pb-2 border-b border-gray-200">
                <span style={monoLabel}>Filter</span>
                <select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  className="border border-gray-300 px-2 py-1 text-xs font-mono"
                  style={{ minWidth: '140px' }}
                >
                  <option value="all">All categories</option>
                  {categories.map(c => (
                    <option key={c.slug} value={c.slug}>{c.icon} {c.name}</option>
                  ))}
                </select>
                <div className="flex gap-1">
                  {['all', 'bold', 'minimal'].map(s => (
                    <button
                      key={s}
                      onClick={() => setStyleFilter(s)}
                      style={{
                        padding: '4px 10px',
                        fontSize: '10px',
                        fontFamily: 'JetBrains Mono, monospace',
                        letterSpacing: '0.1em',
                        textTransform: 'uppercase',
                        border: '1px solid',
                        borderColor: styleFilter === s ? '#0f172a' : '#e2e8f0',
                        background: styleFilter === s ? '#0f172a' : '#ffffff',
                        color: styleFilter === s ? '#ffffff' : '#0f172a',
                        cursor: 'pointer',
                      }}
                    >{s}</button>
                  ))}
                </div>
              </div>

              {/* Template grid · 2 cols mobile · 3 cols desktop · CardCanvas preview real */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {filteredTemplates.map((t) => {
                  const isSelected = selectedTemplateId === t.id;
                  return (
                    <div
                      key={t.id}
                      onClick={() => onTemplateSelect(t.id)}
                      style={{
                        border: `2px solid ${isSelected ? '#01579b' : '#e2e8f0'}`,
                        background: '#ffffff',
                        cursor: 'pointer',
                        transition: 'all 0.15s ease',
                        position: 'relative',
                      }}
                      onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.borderColor = '#94a3b8'; }}
                      onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.borderColor = '#e2e8f0'; }}
                    >
                      {/* CardCanvas real preview */}
                      <div style={{
                        background: '#f8fafc',
                        padding: '14px',
                        display: 'flex',
                        justifyContent: 'center',
                        borderBottom: '1px solid #e2e8f0',
                      }}>
                        <CardCanvas
                          cardData={sample(t)}
                          tier={t.tier || 'signature'}
                          watermark={false}
                          scale={0.6}
                          side="front"
                          showStockBadge={false}
                        />
                      </div>

                      {/* Meta · editorial typography */}
                      <div style={{ padding: '10px 12px' }}>
                        <div style={{
                          display: 'flex', alignItems: 'center',
                          justifyContent: 'space-between', gap: '6px',
                        }}>
                          <div style={editorialTitle}>{t.name}</div>
                          {isSelected && (
                            <div style={{
                              width: '20px', height: '20px',
                              background: '#01579b', color: '#ffffff',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              flexShrink: 0,
                            }}>
                              <Check className="h-3 w-3" />
                            </div>
                          )}
                        </div>
                        <div style={{ ...monoLabel, marginTop: '4px', fontSize: '8px' }}>
                          {t.style} · {t.categorySlug || 'universal'}
                        </div>
                        <div style={{ display: 'flex', gap: '4px', marginTop: '8px' }}>
                          {(t.colors || []).slice(0, 3).map((c, i) => (
                            <div key={i} style={{
                              width: '12px', height: '12px',
                              background: c, border: '1px solid #e2e8f0',
                            }} />
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {filteredTemplates.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  <Sparkles className="h-10 w-10 mx-auto mb-3 opacity-40" />
                  <div style={{ fontSize: '14px', fontWeight: 600 }}>No templates for this filter</div>
                  <div style={{ ...monoLabel, marginTop: '6px' }}>Try changing category or style</div>
                </div>
              )}
            </TabsContent>

            {/* FONTS TAB · compact list */}
            <TabsContent value="fonts" className="space-y-2">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {(fonts || []).map((font) => {
                  const isSelected = selectedFontId === font.id;
                  return (
                    <div
                      key={font.id}
                      onClick={() => onFontSelect(font.id)}
                      style={{
                        border: `2px solid ${isSelected ? '#01579b' : '#e2e8f0'}`,
                        background: '#ffffff',
                        cursor: 'pointer',
                        padding: '12px 14px',
                        transition: 'all 0.15s ease',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ fontFamily: font.family, fontSize: '20px', fontWeight: 700, color: '#0f172a' }}>
                          {font.name}
                        </div>
                        {isSelected && (
                          <div style={{
                            width: '18px', height: '18px',
                            background: '#01579b', color: '#ffffff',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}>
                            <Check className="h-3 w-3" />
                          </div>
                        )}
                      </div>
                      <div style={{
                        fontFamily: font.family,
                        fontSize: '12px',
                        color: '#64748b',
                        marginTop: '2px',
                      }}>
                        The quick brown fox · 0123456789
                      </div>
                      <div style={{ ...monoLabel, marginTop: '6px', fontSize: '8px' }}>
                        {font.category}
                      </div>
                    </div>
                  );
                })}
              </div>
            </TabsContent>
          </Tabs>

          <div className="pt-3 border-t border-gray-200">
            <Button
              onClick={onContinue}
              disabled={!selectedTemplateId || !selectedFontId}
              className="w-full"
              style={{
                background: (!selectedTemplateId || !selectedFontId) ? '#94a3b8' : '#0f172a',
                color: '#ffffff',
                fontFamily: 'JetBrains Mono, monospace',
                fontSize: '11px',
                letterSpacing: '0.15em',
                textTransform: 'uppercase',
                fontWeight: 700,
                height: '44px',
              }}
            >
              Continue · Preview your card
              <Sparkles className="ml-2 h-4 w-4" />
            </Button>
            {(!selectedTemplateId || !selectedFontId) && (
              <div style={{ ...monoLabel, textAlign: 'center', marginTop: '6px', fontSize: '9px' }}>
                Select a template and a font to continue
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DesignCatalog;
